import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';
import { ACTIVE_FILTERS } from 'lumen/utils/constants/market-insight-constants';

const MAX_LENGTH_FILTER_GROUP_NAME = 30;

interface GeneralFilterBarFormsFormFilterBarSaveFavouritesSignature {
  Args: {
    closeModal: unknown;
    hotel: unknown;
    market: unknown;
    pageType: unknown;
    segmentationFilters: unknown;
    stayDateFilter: unknown;
  };
  Element: HTMLDivElement;
}

export default class GeneralFilterBarFormsFormFilterBarSaveFavourites extends Component<GeneralFilterBarFormsFormFilterBarSaveFavouritesSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked filterGroupName = '';

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasValidationErrors() {
    return this.filterGroupName.length > MAX_LENGTH_FILTER_GROUP_NAME;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveButtonDisabled() {
    return (
      !this.filterGroupName.length ||
      this.filterGroupName.length > MAX_LENGTH_FILTER_GROUP_NAME
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  @action submitForm() {
    const {
      closeModal,
      hotel,
      market,
      pageType,
      segmentationFilters,
      stayDateFilter,
    } = this.args;

    const stayDate =
      pageType === FILTER_BAR_PAGETYPES.REVENUE_INSIGHT ? stayDateFilter : null;

    const newRecordData = {
      filters: {
        segmentationFilters,
        stayDateFilter: stayDate,
      },
      hotel,
      market,
      name: this.filterGroupName,
      pageType,
    };

    if (pageType === FILTER_BAR_PAGETYPES.MARKET_INSIGHT) {
      // In case of MI we pass some extra properties to the BE (from_date_range_start & from_date_range_end)
      // BE will use these to automatically remove saved filters that are in the past (MI only supports today + forward looking data)
      try {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsedFilters = JSON.parse(segmentationFilters);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const activeFilters = Object.keys(parsedFilters);

        if (activeFilters.includes(ACTIVE_FILTERS.DAY)) {
          // Example of day filter format: {"day":"2022-09-27"}
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          newRecordData.filters.from_date_range_start =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            parsedFilters[ACTIVE_FILTERS.DAY];
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          newRecordData.filters.from_date_range_end =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            parsedFilters[ACTIVE_FILTERS.DAY];
        } else if (activeFilters.includes(ACTIVE_FILTERS.RANGE)) {
          // Example of range filter format: {"range":"2022-10-04:2022-10-13"}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const splittedRange = parsedFilters[ACTIVE_FILTERS.RANGE].split(':');

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          newRecordData.filters.from_date_range_start = splittedRange[0];
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          newRecordData.filters.from_date_range_end = splittedRange[1];
        }
      } catch (error) {
        this.errorTracking.captureException(error);
      }
    }

    this.usageAnalytics.track('Filter Saved', {
      eventTrigger: 'filter-saver',
    });

    // Note: stayDateFilters are only for RI, not for PA!
    const favourite = this.store.createRecord(
      'insightpagesfavourite',
      newRecordData,
    );

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    favourite
      .save()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .then(
        () => {
          // @ts-expect-error: Incorrect type
          closeModal(favourite.id);
        },
        // @ts-expect-error: Incorrect type
        (response) => {
          // Create a list of errors to display in popup
          const errors = document.createElement('ul');

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (response?.payload?.errors) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            response.payload.errors.forEach((error) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
              errors.innerHTML += `<li>${this.intl.t(error.detail)}</li>`;
            });
          } else {
            errors.innerHTML += `<li>${this.intl.t('general.error_title')}</li>`;
          }

          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.alerting
            .showErrorAlert({
              htmlContent: errors,
            })
            .then(() => {
              // @ts-expect-error: Incorrect type
              this.args.closeModal();
            });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return response;
        },
      )
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert().then(() => {
          // @ts-expect-error: Incorrect type
          this.args.closeModal();
        });

        if (this.errorTracking) {
          this.errorTracking.captureException(e);
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .finally(() => {
        // @ts-expect-error: Incorrect type
        if (favourite.isError) {
          // Make sure that if there was in any case an error, to delete the record
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          favourite.deleteRecord();
        }
      });
  }

  // @ts-expect-error: Incorrect type
  @action updateFilterGroupName(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.filterGroupName = event.target.value;
  }

  <template>
    <div ...attributes>
      <div class="flex w-full flex-row">
        <PrismInput
          @autofocus={{true}}
          @errorMessage={{t
            "validation.max_length"
            maxLength=MAX_LENGTH_FILTER_GROUP_NAME
          }}
          @hasErrors={{this.hasValidationErrors}}
          @label={{t "insight_pages.favourites.input_placeholder"}}
          @maxLength={{MAX_LENGTH_FILTER_GROUP_NAME}}
          @onInput={{this.updateFilterGroupName}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{this.filterGroupName}}
          data-test-id="filter-favourites-input"
        />
      </div>
      <div class="flex justify-center gap-300 p-300">
        <PrismButton
          @buttonType="secondary"
          @label={{t "general.cancel"}}
          {{! @glint-expect-error: Incorrect type }}
          @onClick={{@closeModal}}
          data-test-id="filter-favourites-cancel-button"
        />
        <PrismButton
          @isDisabled={{this.isSaveButtonDisabled}}
          @label={{t "general.save"}}
          @onClick={{this.submitForm}}
          data-test-id="filter-favourites-save-button"
        />
      </div>
    </div>
  </template>
}
